import { useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, Upload, X, ImageIcon } from 'lucide-react';
import { api, CLIENT_ID } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useMe } from '@/lib/hooks';
import { useGuildRights } from '@/lib/access';
import { Popover } from '@/components/Popover';

export interface MediaFile {
  id: string;
  filename: string;
  originalName: string;
  contentType: string;
  size: number;
  uploadedBy: string;
  createdAt: string;
  url: string;
}

/**
 * Popover that shows this guild's uploaded media + an upload zone. On click on
 * an image, calls onSelect(url). Used next to imageUrl / thumbnailUrl / bg-url
 * fields across the config pages.
 */
export function MediaPicker({
  guildId,
  onSelect,
  currentUrl,
  trigger,
}: {
  guildId: string;
  onSelect: (url: string) => void;
  currentUrl?: string;
  trigger?: ReactNode;
}) {
  return (
    <Popover
      width={420}
      trigger={({ onClick, ref }) => (
        <div ref={ref} className="inline-block" onClick={onClick}>
          {trigger ?? (
            <Button variant="outline" size="sm" title="Media">
              <ImageIcon className="h-4 w-4" />
              <span className="ml-1 hidden text-xs sm:inline">Media</span>
            </Button>
          )}
        </div>
      )}
      content={(close) => (
        <MediaPanel
          guildId={guildId}
          currentUrl={currentUrl}
          onSelect={(url) => {
            onSelect(url);
            close();
          }}
          onClose={close}
          variant="popover"
        />
      )}
    />
  );
}

/**
 * The gallery UI itself. Reused by MediaPicker (popover) and MediaLibrary
 * (full-page). Layout adapts via `variant` — the popover is more compact.
 */
export function MediaPanel({
  guildId,
  currentUrl,
  onSelect,
  onClose,
  variant = 'page',
}: {
  guildId: string;
  currentUrl?: string;
  onSelect?: (url: string) => void;
  onClose?: () => void;
  variant?: 'popover' | 'page';
}) {
  const qc = useQueryClient();
  const me = useMe();
  const myId = me.data?.user?.discordId;
  // Deleting others' uploads needs Media-Bibliothek: bearbeiten.
  const canManageMedia = useGuildRights(guildId).can('media', 'edit');

  const listQuery = useQuery({
    queryKey: ['media', guildId],
    queryFn: () => api<{ files: MediaFile[] }>(`/api/guilds/${guildId}/media`),
    enabled: !!guildId,
  });

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api/guilds/${guildId}/media`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Client-Id': CLIENT_ID },
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({ message: `${res.status}` }));
        throw new Error(j.message || j.error || `Upload fehlgeschlagen (${res.status})`);
      }
      return (await res.json()) as { file: MediaFile };
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['media', guildId] });
      if (onSelect) onSelect(r.file.url);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      api(`/api/guilds/${guildId}/media/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media', guildId] }),
  });

  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    uploadMut.mutate(files[0]);
  };

  const files = listQuery.data?.files ?? [];
  const isCompact = variant === 'popover';

  return (
    <div className={cn('flex flex-col', isCompact ? 'max-h-[70vh]' : '')}>
      <div className="flex items-center justify-between border-b p-3">
        <div>
          <div className="text-sm font-semibold">Media-Bibliothek</div>
          <div className="text-xs text-muted-foreground">
            {files.length} Datei{files.length === 1 ? '' : 'en'}
          </div>
        </div>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose} title="Schließen">
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Upload zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={cn(
          'm-3 flex flex-col items-center justify-center rounded-md border-2 border-dashed p-4 text-center',
          dragOver ? 'border-primary bg-primary/5' : 'border-input'
        )}
      >
        <Upload className="mb-1 h-6 w-6 text-muted-foreground" />
        <div className="text-xs text-muted-foreground">
          Hier ablegen oder klicken (PNG / JPG / GIF / WebP, max 10 MB)
        </div>
        <Button
          variant="outline"
          size="sm"
          className="mt-2"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadMut.isPending}
        >
          {uploadMut.isPending ? 'Lade hoch…' : 'Datei wählen'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
        {uploadMut.error && (
          <div className="mt-2 text-xs text-destructive">
            {(uploadMut.error as Error).message}
          </div>
        )}
      </div>

      {/* Gallery */}
      <div
        className={cn(
          'grid gap-2 overflow-y-auto p-3',
          isCompact ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4'
        )}
      >
        {listQuery.isLoading && (
          <div className="col-span-full text-center text-sm text-muted-foreground">
            Lade…
          </div>
        )}
        {!listQuery.isLoading && files.length === 0 && (
          <div className="col-span-full text-center text-sm text-muted-foreground">
            Noch keine Media hochgeladen.
          </div>
        )}
        {files.map((f) => {
          const canDelete = canManageMedia || f.uploadedBy === myId;
          const isCurrent = currentUrl === f.url;
          return (
            <div
              key={f.id}
              className={cn(
                'group relative overflow-hidden rounded-md border bg-muted/30',
                isCurrent && 'ring-2 ring-primary'
              )}
            >
              <button
                type="button"
                onClick={() => onSelect?.(f.url)}
                className="block w-full"
                title={f.originalName}
                disabled={!onSelect}
              >
                <img
                  src={f.url}
                  alt=""
                  loading="lazy"
                  className="aspect-square w-full object-cover"
                />
              </button>
              <div className="pointer-events-none absolute inset-x-0 bottom-0 truncate bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                {f.originalName}
              </div>
              {canDelete && (
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute right-1 top-1 h-6 w-6 opacity-0 group-hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`"${f.originalName}" löschen?`)) {
                      deleteMut.mutate(f.id);
                    }
                  }}
                  title="Löschen"
                  disabled={deleteMut.isPending}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
